/**
 * vs-buy-block.js — Web component voor het Vital Supply koopblok.
 *
 * Beheert variant-selectie, abonnement/eenmalig toggle,
 * dagprijsberekening en add-to-cart.
 * Staat los van Horizon's variantkiezer (Shadow DOM).
 */

class VsBuyBlock extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(
      this.querySelector('[data-variants]')?.textContent || '[]'
    );

    const plansEl = this.querySelector('[data-selling-plans]');
    this.sellingPlans = plansEl ? JSON.parse(plansEl.textContent) : [];

    this.doseringen = parseInt(this.dataset.doseringen, 10) || 0;
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;

    // DOM-referenties
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
    this.priceAmount = this.querySelector('[data-price-amount]');
    this.subPriceEl = this.querySelector('[data-sub-price]');
    this.dagprijsEl = this.querySelector('[data-dagprijs]');
    this.discountBadge = this.querySelector('[data-discount-badge]');
    this.onetimePriceEl = this.querySelector('[data-onetime-price]');
    this.addToCartBtn = this.querySelector('[data-add-to-cart]');
    this.intervalsContainer = this.querySelector('[data-intervals]');
    this.dagprijsCompare = this.querySelector('[data-dagprijs-compare]');
    this.form = this.querySelector('form');
    this.qtyDisplay = this.querySelector('[data-qty-display]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.qtyWrap = this.querySelector('[data-quantity-wrap]');
    this.quantity = 1;

    // Huidige staat
    this.currentVariant = this.variants.find(v => v.id === parseInt(this.variantIdInput?.value, 10))
      || this.variants.find(v => v.available)
      || this.variants[0];

    this.isSubscription = this.querySelector('[data-purchase-type]:checked')?.value === 'subscription';

    this.bindEvents();
    this.updatePurchaseType();
    this.updateUI();
  }

  bindEvents() {
    // Variant-pillen
    this.querySelectorAll('[data-option-index]').forEach(input => {
      input.addEventListener('change', () => this.onVariantChange());
    });

    // Abonnement/eenmalig toggle
    this.querySelectorAll('[data-purchase-type]').forEach(input => {
      input.addEventListener('change', (e) => {
        this.isSubscription = e.target.value === 'subscription';
        this.updatePurchaseType();
        this.updateUI();
      });
    });

    // Leverinterval
    this.querySelectorAll('[data-selling-plan]').forEach(input => {
      input.addEventListener('change', () => this.updateUI());
    });

    // Quantity +/-
    this.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
      if (this.quantity > 1) {
        this.quantity--;
        this.updateQuantity();
      }
    });
    this.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
      if (this.quantity < 99) {
        this.quantity++;
        this.updateQuantity();
      }
    });

    // Add to cart
    this.form?.addEventListener('submit', (e) => this.onSubmit(e));
  }

  updateQuantity() {
    if (this.qtyDisplay) this.qtyDisplay.textContent = this.quantity;
    if (this.qtyInput) this.qtyInput.value = this.quantity;
  }

  onVariantChange() {
    const selectedOptions = [];
    this.querySelectorAll('.vs-buy__variants').forEach(fieldset => {
      const checked = fieldset.querySelector('input:checked');
      if (checked) selectedOptions.push(checked.value);
    });

    // Zoek de variant die bij deze opties hoort
    const match = this.variants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (match) {
      this.currentVariant = match;
      this.updateUI();
    }

    // Update pill-stijlen
    this.querySelectorAll('.vs-buy__pill').forEach(pill => {
      const input = pill.querySelector('input');
      pill.classList.toggle('vs-buy__pill--active', input?.checked);
    });
  }

  updatePurchaseType() {
    // Toon/verberg leverintervallen
    if (this.intervalsContainer) {
      this.intervalsContainer.style.display = this.isSubscription ? '' : 'none';
    }

    // Update toggle-stijlen (toggle-option + plan-card)
    this.querySelectorAll('.vs-buy__toggle-option, .vs-buy__plan-card').forEach(opt => {
      const input = opt.querySelector('input');
      opt.classList.toggle('vs-buy__toggle-option--active', input?.checked);
      opt.classList.toggle('vs-buy__plan-card--active', input?.checked);
    });

    // Reset selling plan als eenmalig
    if (!this.isSubscription && this.sellingPlanInput) {
      this.sellingPlanInput.value = '';
    }

    // Quantity selector: toon bij eenmalig, verberg bij abo
    if (this.qtyWrap) {
      this.qtyWrap.style.display = this.isSubscription ? 'none' : '';
    }
    // Reset quantity bij switch naar abo
    if (this.isSubscription) {
      this.quantity = 1;
      this.updateQuantity();
    }

    // Dynamische CTA-tekst
    if (this.addToCartBtn) {
      this.addToCartBtn.textContent = this.isSubscription ? 'Start je routine met 15% korting' : 'Toevoegen aan winkelwagen';
    }
  }

  updateUI() {
    if (!this.currentVariant) return;

    // Update hidden variant input
    if (this.variantIdInput) {
      this.variantIdInput.value = this.currentVariant.id;
    }

    // Bereken prijzen
    const basePrice = this.currentVariant.price;
    let subscriptionPrice = basePrice;

    // Selling plan prijs — altijd berekenen, ongeacht selectie
    const selectedPlan = this.querySelector('[data-selling-plan]:checked')
      || this.querySelector('[data-single-plan]');
    if (selectedPlan) {
      const plan = this.sellingPlans.find(p => p.id === parseInt(selectedPlan.value, 10));
      if (plan?.price_adjustments?.[0]) {
        const adj = plan.price_adjustments[0];
        if (adj.value_type === 'percentage') {
          subscriptionPrice = Math.ceil(basePrice * (100 - adj.value) / 100 / 10) * 10;
        } else if (adj.value_type === 'fixed_amount') {
          subscriptionPrice = basePrice - adj.value;
        } else if (adj.value_type === 'price') {
          subscriptionPrice = adj.value;
        }
      }
    }

    // Selling plan input: set bij abonnement, leeg bij eenmalig
    if (this.isSubscription && selectedPlan && this.sellingPlanInput) {
      this.sellingPlanInput.value = selectedPlan.value;
    } else if (this.sellingPlanInput) {
      this.sellingPlanInput.value = '';
    }

    // Kwartaalprijs = 3 potten per levering
    const quarterlySubPrice = subscriptionPrice * 3;
    const displayPrice = this.isSubscription ? quarterlySubPrice : basePrice;

    // Update hoofdprijs
    if (this.priceAmount) {
      this.priceAmount.textContent = this.formatMoney(displayPrice);
    }

    // Update abonnementsprijs in plan-card (kwartaaltotaal als hoofdprijs)
    if (this.subPriceEl) {
      this.subPriceEl.textContent = this.formatMoney(quarterlySubPrice);
    }

    // Update eenmalige prijs in plan-card
    if (this.onetimePriceEl) {
      this.onetimePriceEl.textContent = this.formatMoney(basePrice);
    }

    // Update kortingsbadge
    if (this.discountBadge && basePrice > 0) {
      const discountPct = Math.round((basePrice - subscriptionPrice) / basePrice * 100);
      if (discountPct > 0) {
        this.discountBadge.textContent = `Bespaar ${discountPct}%`;
        this.discountBadge.style.display = '';
      } else {
        this.discountBadge.style.display = 'none';
      }
    }

    // Update sub-tekst met per-pot prijs
    const subTextEl = this.querySelector('.vs-buy__plan-card-sub');
    if (subTextEl) {
      subTextEl.textContent = `3 potten · ${this.formatMoney(subscriptionPrice)} per pot`;
    }

    // Update dagprijs / kwartaal context
    if (this.dagprijsEl) {
      if (this.isSubscription) {
        this.dagprijsEl.textContent = `per kwartaal · ${this.formatMoney(subscriptionPrice)} per pot`;
      } else if (this.doseringen > 0) {
        const dagprijs = basePrice / this.doseringen;
        this.dagprijsEl.textContent = `${this.formatMoney(dagprijs)} per dag`;
      }
    }

    // Update dagprijsvergelijking (legacy)
    if (this.dagprijsCompare && this.doseringen > 0) {
      const dagprijsEenmalig = basePrice / this.doseringen;
      const dagprijsAbonnement = subscriptionPrice / this.doseringen;

      if (this.sellingPlans.length > 0 && subscriptionPrice < basePrice) {
        this.dagprijsCompare.innerHTML =
          `<span>${this.formatMoney(dagprijsAbonnement)} per dag met abonnement</span>` +
          `<span style="margin-left: calc(var(--vs-space-unit) * 2); text-decoration: line-through; opacity: 0.5;">${this.formatMoney(dagprijsEenmalig)}</span>`;
      } else {
        this.dagprijsCompare.innerHTML =
          `<span>${this.formatMoney(dagprijsEenmalig)} per dag</span>`;
      }
    }

    // Update add-to-cart knop
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !this.currentVariant.available;
      this.addToCartBtn.textContent = this.currentVariant.available
        ? (this.isSubscription ? 'Start je routine met 15% korting' : 'Toevoegen aan winkelwagen')
        : 'Uitverkocht';
    }

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', this.currentVariant.id);
    history.replaceState(null, '', url);
  }

  async onSubmit(e) {
    e.preventDefault();

    const btn = this.addToCartBtn;
    if (!btn || btn.disabled) return;

    btn.disabled = true;
    btn.textContent = 'Toevoegen...';

    try {
      const formData = new FormData(this.form);

      // Bij abonnement: quantity 3 (kwartaallevering = 3 potten)
      if (this.isSubscription) {
        formData.set('quantity', '3');
      }

      const res = await fetch(window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cart/add.js`
        : '/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error('Toevoegen mislukt');

      // Direct naar cart pagina
      window.location.href = '/cart';

      btn.textContent = 'Toegevoegd ✓';
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = this.isSubscription ? 'Start je routine met 15% korting' : 'Toevoegen aan winkelwagen';
      }, 1500);
    } catch (err) {
      console.error('VS Buy Block:', err);
      btn.disabled = false;
      btn.textContent = this.isSubscription ? 'Start je routine met 15% korting' : 'Toevoegen aan winkelwagen';
    }
  }

  formatMoney(cents) {
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `€${euros}`;
  }
}

if (!customElements.get('vs-buy-block')) {
  customElements.define('vs-buy-block', VsBuyBlock);
}
